import React from "react";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import TwitterIcon from "@mui/icons-material/Twitter";
import { Link as RouterLink } from "react-router-dom";
import SecureConnected from "../components/SecureConnected";
import { SiteFooter, SiteHeader } from "../components/SiteChrome";
import useScrollReveal from "../hooks/useScrollReveal";
import "./Contact.css";

const contactMethods = [
  {
    icon: <ChatBubbleOutlineRoundedIcon />,
    title: "Live Chat",
    copy: "Get instant help through our in-app chat",
    meta: "Mon-Fri, 9AM-6PM",
    action: "Open App",
    href: "/#download",
  },
  {
    icon: <LocalPhoneOutlinedIcon />,
    title: "Call Us",
    copy: "Smart: 0923 857 0216 | Globe: 0945 833 2192",
    meta: "Mon-Fri, 9AM-6PM",
    action: "Call Now",
    href: "tel:+639238570216",
  },
  {
    icon: <EmailOutlinedIcon />,
    title: "Email Support",
    copy: "corporate@trilloan.com",
    meta: "Response within 24hrs",
    action: "Send Email",
    href: "mailto:corporate@trilloan.com",
  },
];

const helpCategories = [
  ["General Question", "Account Setup, app features, how-to guides"],
  ["Security & Privacy", "Account security, fraud reports, privacy concern"],
  ["Loans & Payment", "Loan applications, payment issues, transaction help"],
  ["Technical Support", "App problem, Login issues, troubleshooting"],
];

export default function Contact() {
  useScrollReveal();

  return (
    <main className="contact-page">
      <SiteHeader />

      <section className="contact-hero tl-reveal">
        <div className="contact-heading">
          <h1>We're Here to Help</h1>
          <p>
            Have a question or need assistance? Our support team is ready to help
            you with anything you need, from account setup to loan applications.
          </p>
        </div>

        <h2>Choose How You'd Like to Connect</h2>
        <div className="contact-method-grid">
          {contactMethods.map((method, index) => (
            <article className={`tl-reveal tl-reveal-delay-${index % 3}`} key={method.title}>
              <span className="contact-circle-icon">{method.icon}</span>
              <h3>{method.title}</h3>
              <p>{method.copy}</p>
              <div className="contact-meta">
                <AccessTimeOutlinedIcon />
                {method.meta}
              </div>
              <a href={method.href}>{method.action}</a>
            </article>
          ))}
        </div>

        <h2 className="quick-help-title">Quick Help Categories</h2>
        <div className="quick-help-grid">
          {helpCategories.map(([title, copy], index) => (
            <article className={`tl-reveal tl-reveal-delay-${index % 3}`} key={title}>
              <span className="contact-circle-icon">
                <ChatBubbleOutlineRoundedIcon />
              </span>
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="message-section tl-reveal">
        <div className="message-layout">
          <form className="message-form">
            <h2>Send Us a Message</h2>
            <div className="form-row">
              <label>
                Your name
                <input type="text" placeholder="Jhon Doe" />
              </label>
              <label>
                Email address
                <input type="email" placeholder="example@gmail" />
              </label>
            </div>
            <label>
              What can we help you with?
              <select defaultValue="">
                <option value="" disabled>
                  Select a topic
                </option>
                <option>General Question</option>
                <option>Security & Privacy</option>
                <option>Loans & Payment</option>
                <option>Technical Support</option>
              </select>
            </label>
            <label>
              Your Message
              <textarea placeholder="Please describe your question or concern in detail..." />
            </label>
            <button type="submit">Send Message</button>
          </form>

          <aside className="contact-info-stack">
            <article className="contact-info-card office-card">
              <h3>Visit Our Office</h3>
              <div className="info-row">
                <LocationOnIcon />
                <div>
                  <strong>Address</strong>
                  <p>Unit 404-406 AD Center Square,Evangelista St., Santolan, Pasig City. 1610</p>
                </div>
              </div>
              <div className="info-row">
                <AccessTimeOutlinedIcon />
                <div>
                  <strong>Business Hours</strong>
                  <p>Monday - Friday: 9:00 AM - 6:00 PM (PHT)</p>
                  <p>Saturday - Sunday: Closed</p>
                </div>
              </div>
            </article>

            <article className="contact-info-card follow-card">
              <h3>Follow Us</h3>
              <p>Stay updated with our latest news and announcements</p>
              <div className="contact-socials">
                <a href="https://www.facebook.com/profile.php?id=61581222758581" aria-label="Facebook">
                  <FacebookRoundedIcon />
                </a>
                <a href="https://twitter.com/paychat_ph" aria-label="Twitter">
                  <TwitterIcon />
                </a>
                <a href="https://www.instagram.com/paychatph/" aria-label="Instagram">
                  <InstagramIcon />
                </a>
                <a href="https://www.trilloan.com/" aria-label="Website">
                  <PublicRoundedIcon />
                </a>
              </div>
            </article>

            <article className="contact-info-card faq-card">
              <span className="contact-circle-icon">
                <ChatBubbleOutlineRoundedIcon />
              </span>
              <h3>Need Quick Answers?</h3>
              <p>Check our FAQ section for instant answers to common questions</p>
              <RouterLink to="/help-center">Browse FAQ's</RouterLink>
            </article>
          </aside>
        </div>
      </section>

      <SecureConnected />
      <SiteFooter />
    </main>
  );
}
